import * as React from "react";
import apiClients from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { parseId } from "../../helpers/idHelper";
import { getTagsMap } from "../../helpers/resourceHelper";
import { useMutationCall } from "../../hooks/useMutationCall";
import {
  AsyncNotificationMethodKey,
  AsyncNotificationPolledResponseKey,
  EXADB_PDB_UPDATE_POLL_DELAY,
  EXADB_PDB_UPDATE_POLL_INTERVAL,
} from "../../models/AsyncNotificationProviders";
import { getOciRegion } from "../../utils";
import { OperationConfirmationProps } from "../OperationTypes";

export interface ExaDbUpdateTagsPdbProps extends OperationConfirmationProps {
  databaseId: string;
  tags: { name: string; value: string; }[];
}

const ExaDbUpdateTagsPdb = (
  { location, databaseId, tags, onExecute }: ExaDbUpdateTagsPdbProps,
): JSX.Element => {
  const { invokeCall: invokePdbUpdate } = useMutationCall(
    { method: apiClients.withRegion(getOciRegion(location)).exaDatabaseApi.updatePluggableDatabase },
  );

  React.useEffect(() => {
    const { subscriptionId, resourceGroup, resourceName } = parseId(databaseId);
    const resGroup = decodeURIComponent(resourceGroup || "");
    const pdbName = decodeURIComponent(resourceName || "");

    const invokeOptions = {
      onSuccess: onExecute,
      notification: {
        inProgress: {
          title: Messages.notifications.inProgress.titles.applyTags(),
          message: Messages.notifications.inProgress.messages.applyTags(pdbName),
        },
        success: {
          title: Messages.notifications.success.titles.applyTags(),
          message: Messages.notifications.success.messages.applyTags(pdbName),
        },
        failure: {
          title: Messages.notifications.failure.titles.applyTags(),
          message: Messages.notifications.failure.messages.applyTags(pdbName),
        },
        asyncPolling: {
          methodKey: AsyncNotificationMethodKey.EXADB_PDB_GET,
          methodArgs: {
            subscriptionId,
            resourceGroupName: resGroup,
            pluggableDatabaseName: pdbName,
          },
          location,
          responseMethodKey: AsyncNotificationPolledResponseKey.EXADB_UPDATED_CHECK_PDB,
          delay: EXADB_PDB_UPDATE_POLL_DELAY,
          interval: EXADB_PDB_UPDATE_POLL_INTERVAL,
        },
      },
    };
    invokePdbUpdate({
      subscriptionId,
      resourceGroupName: resGroup,
      pluggableDatabaseName: pdbName,
      updatePluggableDatabaseDetails: { freeformTags: getTagsMap(tags) },
    }, invokeOptions);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div />;
};

export const newExaDbUpdateTagsPdb = (
  props: ExaDbUpdateTagsPdbProps,
): JSX.Element => (<ExaDbUpdateTagsPdb {...props} />);
